import React from 'react'
import { FilePdf } from 'phosphor-react'
import { Ring, ScoreLine, FindingsBars, StatusDonut } from './charts'
import { Card, InboxCard, InboxGroups, ProfileStrip } from './Shared'
import {
    fmt,
    formatTime,
    formatDay,
    formatLongDate,
    greeting,
    firstName,
    scoreClass,
    findingsClass,
    heatColorForItem,
    heatColorForScore,
    activityClass,
} from './format'

const Gauge = ({ hero, caps, unit, unitTitle: Unit, days }) => {
    // A company that scores keeps the score gauge for every period (7 / 30 / 90 days). Before, a period without a
    // scored audit switched the hero to "completed / planned", so changing the period changed the whole layout.
    if (caps.Scoring && (hero.Score === null || hero.Score === undefined)) {
        return (
            <div className="gauge">
                <Ring value={null} />
                <div>
                    <div className="lbl">Compliance score</div>
                    <div className="big">–</div>
                    <div className="delta">{`No scored ${unit(2)} in the last ${days} days`}</div>
                </div>
            </div>
        )
    }
    if (caps.Scoring) {
        const delta =
            hero.ScorePrevious !== null && hero.ScorePrevious !== undefined
                ? Math.round((hero.Score - hero.ScorePrevious) * 10) / 10
                : null
        return (
            <div className="gauge">
                <Ring value={hero.Score} />
                <div>
                    <div className="lbl">Compliance score</div>
                    <div className="big">
                        {hero.Score}
                        <small>%</small>
                    </div>
                    <div className={`delta${delta !== null && delta < 0 ? ' bad' : ''}`}>
                        {delta === null
                            ? `average of scored ${unit(2)}`
                            : `${delta >= 0 ? '+' : ''}${delta} pts vs previous ${days} days`}
                    </div>
                </div>
            </div>
        )
    }
    if (caps.Schedule && hero.Planned > 0) {
        const pct = Math.round((hero.Completed / Math.max(hero.Planned, 1)) * 100)
        return (
            <div className="gauge">
                <Ring value={pct} label="of plan" />
                <div>
                    <div className="lbl">{Unit()} completed</div>
                    <div className="big">
                        {fmt(hero.Completed)}
                        <small>/ {fmt(hero.Planned)} planned</small>
                    </div>
                    <div className="delta">{fmt(hero.DueThisPeriod)} still due</div>
                </div>
            </div>
        )
    }
    const clean = hero.Completed
        ? Math.round(((hero.Completed - hero.AuditsWithFindings) / hero.Completed) * 100)
        : 0
    const diff = hero.Completed - (hero.CompletedPrevious || 0)
    return (
        <div className="gauge">
            <Ring value={clean} label="clean" />
            <div>
                <div className="lbl">{Unit()} completed</div>
                <div className="big">
                    {fmt(hero.Completed)}
                    <small>last {days} days</small>
                </div>
                <div className={`delta${diff < 0 ? ' bad' : ''}`}>
                    {diff >= 0 ? `${fmt(diff)} more` : `${fmt(-diff)} fewer`} than the {days} days before · {clean}% had no findings
                </div>
            </div>
        </div>
    )
}

const heroStats = (hero, caps, unit) => {
    const stats = []
    if (caps.HighVolume) stats.push({ k: 'today', v: fmt(hero.Today), l: `${unit(2)} today`, x: '' })
    stats.push({
        k: 'findings',
        v: fmt(hero.FindingsRaised),
        l: 'Findings raised',
        x: `in ${fmt(hero.AuditsWithFindings)} of ${fmt(hero.Completed)} ${unit(hero.Completed)}`,
    })
    if (caps.Tickets) {
 stats.push({
            k: 'tickets',
            v: fmt(hero.FindingsOpen),
            l: 'Open tickets',
            x: `${fmt(hero.FindingsPastDue)} past due · ${fmt(hero.Escalated)} escalated`,
            c: hero.FindingsPastDue || hero.Escalated ? 'bad' : 'ok',
        })
} else {
 stats.push({
            k: 'open',
            v: fmt(hero.FindingsOpen),
            l: 'Still open',
            x: `${fmt(hero.FindingsPastDue)} past due`,
            c: hero.FindingsPastDue ? 'bad' : 'ok',
        })
}
    if (caps.Workflow) {
 stats.push({
            k: 'waiting',
            v: fmt((hero.WaitingPhases || 0) + (hero.WaitingTickets || 0)),
            l: 'Waiting on me',
            x: `${fmt(hero.WaitingPhases)} phases · ${fmt(hero.WaitingTickets)} tickets`,
        })
} else if (caps.Tickets) stats.push({ k: 'waiting', v: fmt(hero.WaitingTickets), l: 'Waiting on me', x: 'tickets assigned to me' })
    if (caps.Sites) {
 stats.push({
            k: 'sites',
            v: (
                <>
                    {fmt(hero.SitesWithOpenFindings)}
                    <small>/ {fmt(hero.SitesTotal)}</small>
                </>
            ),
            l: caps.Sites === 'customer' ? 'Sites with open findings' : 'Templates with open findings',
            x: hero.Escalated ? `${fmt(hero.Escalated)} escalated` : '',
            c: hero.Escalated ? 'bad' : '',
        })
}
    if (caps.Invoices) {
 stats.push({
            k: 'invoices',
            v: fmt(hero.InvoicesUnpaid),
            l: 'Unpaid invoices',
            x: `${fmt(hero.InvoicesUnpaidAmount)} · ${fmt(hero.InvoicesDueThisWeek)} due this week`,
            c: 'warn',
        })
}
    return stats.slice(0, 5)
}

const LEGENDS = {
    score: [['bad', 'below 75'], ['warn', '75 to 84'], ['ok', '85 to 89'], ['great', '90 and above']],
    rate: [['bad', '75+ per 100 or escalated'], ['warn', '40 to 74'], ['ok', 'below 40'], ['great', 'none open']],
    count: [['bad', '3+ open or escalated'], ['warn', '1 to 2 open'], ['ok', 'none open'], ['great', 'no findings']],
}

const HeatLegend = ({ scored, rate }) => {
    let rows = LEGENDS.count
    if (scored) rows = LEGENDS.score
    else if (rate) rows = LEGENDS.rate
    return rows.map(([k, label]) => (
        <span key={k}>
            <i style={{ background: `var(--hd-heat-${k})` }} />
            {label}
        </span>
    ))
}

const emailClass = status => {
    if (/failed/i.test(status || '')) return 'bad'
    if (/^sent/i.test(status || '')) return 'info'
    return 'gray'
}

const SiteHealthCard = ({ site, caps, unit, nav }) => {
    const isCustomer = caps.Sites === 'customer'
    const title = isCustomer ? 'Site health' : 'Template health'
    const what = isCustomer ? 'site' : 'template'
    const items = site?.Items || []
    const rate = site?.Mode === 'rate'
    const scored = caps.Scoring && items.some(i => i.Score !== null && i.Score !== undefined)
    let sub = `Open findings per ${what}. Red has 3 or more open or an escalation, amber 1 to 2, green none.`
    if (scored) sub = `Average score per ${what}.`
    else if (rate) sub = `Open findings per 100 ${unit(2)}, by ${what}. Red is 75 or more or an escalation, amber 40 to 74, green below 40.`
    return (
        <Card title={title} sub={sub}>
            {items.length === 0 ? (
                <div className="empty">No completed {unit(2)} with a {what} in this period.</div>
            ) : (
                <>
                    <div className="heat">
                        {items.map(s => (
                            <button
                                type="button"
                                key={s.Id}
                                style={{ background: scored ? heatColorForScore(s.Score) : heatColorForItem(s, site.Mode) }}
                                onClick={nav.openTasks}
                                title={s.Name}
                            >
                                <span className="n">{s.Name}</span>
                                {scored ? (
                                    <span className="s">{s.Score === null || s.Score === undefined ? '–' : `${s.Score}%`}</span>
                                ) : (
                                    <span className="s">
                                        {rate ? fmt(s.RatePer100) : fmt(s.Open)}
                                        <small>{rate ? 'per 100' : 'open'}</small>
                                    </span>
                                )}
                                <span className="o">
                                    {scored || rate ? `${fmt(s.Open)} open · ` : `${fmt(s.Findings)} findings · `}
                                    {fmt(s.Audits)} {unit(s.Audits)}
                                    {s.Escalated ? <b>{` · ${fmt(s.Escalated)} escalated`}</b> : null}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="legend">
                        <HeatLegend scored={scored} rate={rate} />
                        <span style={{ marginLeft: 'auto' }}>
                            {items.length >= 12 ? 'Top 12, ' : ''}sorted most open first
                        </span>
                    </div>
                </>
            )}
        </Card>
    )
}

// Score column when the company scores (no score = "–"), findings pill otherwise
const ResultCell = ({ row, scoring }) => {
    if (scoring) {
        if (row.Score === null || row.Score === undefined) return <span className="score">–</span>
        return <span className={`score ${scoreClass(row.Score)}`}>{row.Score}%</span>
    }
    return <span className={`pill ${findingsClass(row.Findings)}`}>{row.Findings ? fmt(row.Findings) : 'None'}</span>
}

const RecentReportsCard = ({ rows, caps, unit, unitTitle: Unit, nav }) => (
    <Card title={`Recent ${unit(2)}`} sub="Completed reports and who they were sent to" action="Tasks" onAction={nav.openTasks}>
        {!rows?.length ? (
            <div className="empty">No completed {unit(2)} in this period.</div>
        ) : (
            <table className="tbl">
                <thead>
                    <tr>
                        <th>{Unit(1)}</th>
                        <th className="num">{caps.Scoring ? 'Score' : 'Findings'}</th>
                        <th>Email</th>
                        <th className="num">PDF</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.AuditId}>
                            <td>
                                <div style={{ fontWeight: 600 }}>{r.Name}</div>
                                <div className="sub">
                                    {[r.Customer, r.Auditor, formatDay(r.CompletedOn)].filter(Boolean).join(' · ')}
                                </div>
                            </td>
                            <td className="num">
                                <ResultCell row={r} scoring={caps.Scoring} />
                            </td>
                            <td>
                                <span
                                    className={`pill ${emailClass(r.EmailStatus)}`}
                                >
                                    {r.EmailStatus || 'Unknown'}
                                </span>
                            </td>
                            <td className="num">
                                {r.HasPdf && (
                                    <button type="button" className="hd-btn sm o" title="Open PDF" onClick={() => nav.openReport(r)}>
                                        <FilePdf size={14} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </Card>
)

// Backend InvoiceStatus (Invoices/InvoiceDtos.cs)
const INVOICE_STATUS = {
    0: ['Draft', 'gray'],
    1: ['Sent', 'info'],
    2: ['Partly paid', 'warn'],
    3: ['Paid', 'ok'],
    4: ['Overdue', 'bad'],
    5: ['Cancelled', 'gray'],
    6: ['Credited', 'gray'],
}

// Backend notes are written for developers; show only the ones that matter to an admin, in plain words.
const userNotes = notes =>
    (notes || [])
        .map(n => {
            const t = String(n || '')
            if (/FindingAnswers/i.test(t)) return "Findings use the built-in answer list. Add your company's own answers in Settings → Home Dashboard."
            if (/Email history is not installed/i.test(t)) return 'Report email status is not available yet.'
            return ''
        })
        .filter(Boolean)

export const LeadershipView = ({ data, user, range, onRange, customerId, onCustomer, customers, nav, canEdit, unit, unitTitle: Unit }) => {
    const caps = data.Capabilities || {}
    const hero = data.Hero || {}
    const days = data?.Window?.Days || range
    const inboxSide = Boolean(caps.Team || caps.Tickets || caps.Workflow)
    const name = firstName(user)
    const scopeName = customerId
        ? (customers || []).find(c => String(c.value) === String(customerId))?.text || 'one customer'
        : 'all customers'
    const stats = heroStats(hero, caps, unit)
    const scoringNote = caps.Scoring
        ? `Scores shown: this company scores its ${unit(2)}`
        : `No scores: this company does not score its ${unit(2)}, so findings are shown instead`

    return (
        <>
            <section className="hero">
                <div className="greet">
                    <div>
                        <h1>
                            {greeting()}
                            {name ? `, ${name}` : ''}
                        </h1>
                        <div className="sub">
                            {formatLongDate()} · {scopeName} · last {days} days
                        </div>
                        <span className="modechip">{scoringNote}</span>
                    </div>
                    <div className="ctl">
                        {(customers || []).length > 1 && (
                            <select
                                aria-label="Customer"
                                value={customerId || ''}
                                onChange={e => onCustomer(e.target.value ? Number(e.target.value) : null)}
                            >
                                <option value="">All customers</option>
                                {customers.map(c => (
                                    <option key={c.value} value={c.value}>
                                        {c.text}
                                    </option>
                                ))}
                            </select>
                        )}
                        <span className="seg onhero" role="group" aria-label="Period">
                            {[7, 30, 90].map(r => (
                                <button type="button" key={r} aria-pressed={r === range} onClick={() => onRange(r)}>
                                    {r}d
                                </button>
                            ))}
                        </span>
                        <button type="button" className="hd-btn w" onClick={nav.startNew}>
                            Start {unit(1)}
                        </button>
                    </div>
                </div>
                <div className="main">
                    <Gauge hero={hero} caps={caps} unit={unit} unitTitle={Unit} days={days} />
                    <div className="stats">
                        {stats.map(s => (
                            <button type="button" key={s.k} onClick={s.k === 'tickets' || s.k === 'waiting' ? () => nav.navigate('/app/tickets') : nav.openTasks}>
                                <span className="v">{s.v}</span>
                                <span className="l">{s.l}</span>
                                {s.x && <span className={`x ${s.c || ''}`}>{s.x}</span>}
                            </button>
                        ))}
                    </div>
                </div>
            </section>
            <ProfileStrip data={data} unit={unit} canEdit={canEdit} onSettings={nav.openSettings} />
            {canEdit && userNotes(data.Notes).length > 0 && <div className="notice">{userNotes(data.Notes).join(' ')}</div>}

            <div className={`body${inboxSide ? '' : ' single'}`}>
                <div className="col">
                    {caps.Sites && data.SiteHealth ? (
                        <SiteHealthCard site={data.SiteHealth} caps={caps} unit={unit} nav={nav} />
                    ) : (
                        <div className="emptycard">
                            <div>
                                <b>One customer and few templates.</b> Site and template health shows once there is more
                                than one customer or at least 3 templates in use.
                            </div>
                        </div>
                    )}
                    <div className="two">
                        {data.Trend && (
                            <Card
                                title={data.Trend.Kind === 'score' ? 'Score trend' : 'Findings per week'}
                                sub={
                                    data.Trend.Kind === 'score'
                                        ? 'Daily average, target dashed'
                                        : `Raised on completed ${unit(2)}, last 12 weeks · dashed line is the average`
                                }
                            >
                                {data.Trend.Kind === 'score' ? (
                                    <ScoreLine points={data.Trend.Points} />
                                ) : (
                                    <FindingsBars points={data.Trend.Points} />
                                )}
                            </Card>
                        )}
                        {data.StatusMix && (
                            <Card
                                title={`${Unit()} status`}
                                sub={caps.Schedule ? 'This period, incl. scheduled tasks' : 'This period'}
                                action="Tasks"
                                onAction={nav.openTasks}
                            >
                                <StatusDonut mix={data.StatusMix} unitWord={unit(2)} />
                                {!caps.Schedule && (
                                    <div className="empty" style={{ marginTop: 10 }}>
                                        No schedules yet. Overdue can't be tracked until {unit(2)} are planned.
                                    </div>
                                )}
                            </Card>
                        )}
                    </div>
                    {caps.Team && (
                        <div className="two">
                            <Card title="Activity" sub="Today, newest first">
                                {!data.Activity?.length ? (
                                    <div className="empty">Nothing yet today.</div>
                                ) : (
                                    <ul className="stream">
                                        {data.Activity.map((e, i) => (
                                            <li key={`${e.At}-${i}`}>
                                                <time>{formatTime(e.At)}</time>
                                                <span className={`d ${activityClass(e.Kind)}`} />
                                                <div>
                                                    <button type="button" className="hd-link" onClick={() => nav.openActivity(e)}>
                                                        {e.Title}
                                                    </button>
                                                    {e.Subtitle && <div className="s">{e.Subtitle}</div>}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>
                            <Card title="Team" sub={`${unit(2)} and findings raised, last ${days} days`}>
                                {!data.Team?.length ? (
                                    <div className="empty">No completed {unit(2)} in this period.</div>
                                ) : (
                                    <table className="tbl">
                                        <thead>
                                            <tr>
                                                <th>Auditor</th>
                                                <th className="num">{Unit()}</th>
                                                <th className="num">Findings</th>
                                                {caps.Scoring && <th className="num">Score</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.Team.map(t => (
                                                <tr key={t.UserId}>
                                                    <td style={{ fontWeight: 600 }}>{t.Name}</td>
                                                    <td className="num">{fmt(t.Audits)}</td>
                                                    <td className="num">
                                                        <span className={`pill ${findingsClass(t.Findings)}`}>{fmt(t.Findings)}</span>
                                                    </td>
                                                    {caps.Scoring && (
                                                        <td className="num">
                                                            {t.Score === null || t.Score === undefined ? '–' : (
                                                                <span className={`score ${scoreClass(t.Score)}`}>{t.Score}%</span>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </Card>
                        </div>
                    )}
                    <RecentReportsCard rows={data.RecentReports} caps={caps} unit={unit} unitTitle={Unit} nav={nav} />
                    {caps.Invoices && data.Invoices && (
                        <Card
                            title="Invoices"
                            sub={`${fmt(data.Invoices.Unpaid)} unpaid · ${fmt(data.Invoices.Overdue)} overdue · ${fmt(data.Invoices.Draft)} draft`}
                            action="All"
                            onAction={() => nav.navigate('/app/invoice')}
                        >
                            {!data.Invoices.Recent?.length ? (
                                <div className="empty">No invoices yet.</div>
                            ) : (
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Invoice</th>
                                            <th>Customer</th>
                                            <th className="num">Outstanding</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.Invoices.Recent.map(inv => {
                                            const st = INVOICE_STATUS[inv.Status] || ['–', 'gray']
                                            return (
                                                <tr key={inv.InvoiceId}>
                                                    <td>{inv.Number}</td>
                                                    <td>{inv.Customer}</td>
                                                    <td className="num">{fmt(inv.Outstanding)}</td>
                                                    <td>
                                                        <span className={`pill ${st[1]}`}>{st[0]}</span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </Card>
                    )}
                </div>
                {inboxSide && <InboxCard groups={data.Inbox} nav={nav} canEdit={canEdit} onSettings={nav.openSettings} />}
            </div>
            {!inboxSide && (data.Inbox || []).some(g => g.Count > 0) && (
                <div className="card" style={{ marginTop: 18 }}>
                    <div className="card-h">
                        <div>
                            <h3>Needs attention</h3>
                        </div>
                    </div>
                    <div className="inbox">
                        <InboxGroups groups={data.Inbox.filter(g => g.Count > 0)} nav={nav} />
                    </div>
                </div>
            )}
        </>
    )
}
